import { useCallback, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { AppScreen, Device, Organization, Store } from '@/types/common';
import { useDevicePairing } from './useDevicePairing';
import { storageSvc } from '@/services/storageServiceClient';
import { useSelection } from '@/libs/onboarding';
import { useDeviceUnpair } from './useDeviceUnpair';

/**
 * Hook that manages the completion phase of the onboarding process, specifically
 * handling device pairing with intelligent conflict resolution.
 *
 * Flow:
 * 1. **Same Device**: If fingerprints match → Navigate directly to dashboard
 * 2. **Unpaired Device**: Standard pairing flow → Navigate to dashboard on success
 * 3. **Conflicted Device**: Show alert → User can confirm to unpair + pair → Navigate to dashboard
 *
 * @returns Hook interface with pairing states, error handling, and completion handler
 */
interface UseOnboardingCompletionReturn {
  isDevicePairing: boolean;
  error: string | null;
  hasPairingConflict: boolean;
  clearError: () => void;
  handleOnboardingCompletion: (
    organization: Organization,
    store: Store,
    device: Device,
  ) => Promise<void>;
  dismiss: { action: () => Promise<void> };
  confirm: { action: () => Promise<void> };
}

export const useOnboardingCompletion = (): UseOnboardingCompletionReturn => {
  const [hasPairingConflict, setHasPairingConflict] = useState(false);
  const alertDeviceRef = useRef<Device | null>(null);
  const alertStoreRef = useRef<Store | null>(null);

  const navigate = useNavigate();
  const {
    isDevicePairing,
    pairingError,
    pairDevice,
    clearError: clearPairingError,
    getStoredFingerprint,
    storeFingerprint,
  } = useDevicePairing();
  const {
    unpairDevice,
    isDeviceUnpairing,
    unpairError,
    clearError: clearUnPairingError,
  } = useDeviceUnpair();
  const { changeDevice, selectDevice } = useSelection(storageSvc);

  const dismissAlert = useCallback(async () => {
    setHasPairingConflict(false);
    if (alertDeviceRef.current) {
      await changeDevice();
    }
    alertDeviceRef.current = null;
    alertStoreRef.current = null;
  }, [changeDevice]);

  const pairDeviceAndHandleResult = useCallback(
    async (store: Store, device: Device) => {
      const result = await pairDevice(store, device);

      if (!result.success) {
        await changeDevice();
        return;
      }

      await selectDevice(device);

      navigate(AppScreen.DASHBOARD, { replace: true });
    },
    [pairDevice, selectDevice, navigate, changeDevice],
  );

  const confirmDeviceUnpairingAndPair = useCallback(async () => {
    const device = alertDeviceRef.current;
    const store = alertStoreRef.current;

    if (!device || !store) {
      // No device or store to unpair, just dismiss the alert
      await dismissAlert();
      return;
    }

    // Before unpairing, dismiss the alert, to show loading feedback
    await dismissAlert();

    try {
      const unpaired = await unpairDevice(device);
      if (!unpaired) {
        return;
      }
      await pairDeviceAndHandleResult(store, device);
    } catch (error) {
      // Handle unpairDevice rejection - don't proceed to pairing
      console.error('Error during device unpairing:', error);
      return;
    }
  }, [dismissAlert, unpairDevice, pairDeviceAndHandleResult]);

  const handleOnboardingCompletion = useCallback(
    async (_: Organization, store: Store, device: Device) => {
      if (device.isPaired) {
        // device is already paired, verify if it's the same device by comparing fingerprints
        const storedFingerprint = await getStoredFingerprint();

        if (storedFingerprint && device.uuid === storedFingerprint) {
          // fingerprints match, same device - update fingerprint in storage
          await storeFingerprint(storedFingerprint);
          navigate(AppScreen.DASHBOARD, { replace: true });
          return;
        }
        // Different device or no stored fingerprint
        alertDeviceRef.current = device;
        alertStoreRef.current = store;
        setHasPairingConflict(true);
        return;
      }
      await pairDeviceAndHandleResult(store, device);
    },
    [
      pairDeviceAndHandleResult,
      getStoredFingerprint,
      storeFingerprint,
      navigate,
    ],
  );

  const dismiss = useMemo(() => ({ action: dismissAlert }), [dismissAlert]);
  const confirm = useMemo(
    () => ({ action: confirmDeviceUnpairingAndPair }),
    [confirmDeviceUnpairingAndPair],
  );

  const clearError = useCallback(() => {
    clearPairingError();
    clearUnPairingError();
  }, [clearPairingError, clearUnPairingError]);

  return {
    isDevicePairing: isDevicePairing || isDeviceUnpairing,
    error: pairingError || unpairError,
    clearError,
    handleOnboardingCompletion,
    hasPairingConflict,
    dismiss,
    confirm,
  };
};
